import { useCallback, useState } from 'react';
import { setNestedObjectValues, useFormik } from 'formik';
import { useShallow } from 'zustand/react/shallow';
import {
  CONTEXT_MESSAGES,
  INITIAL_LEAD_CAPTURE_VALUES,
  PROVINCE_ABBREVIATION_MAP,
  PROVINCES_OPTIONS,
} from '@constants/leadCaptureConstants';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { LeadCaptureReason } from '@t/serviceQualification.types';
import { useSubmitLeadCapture } from '@hooks/useSubmitLeadCapture';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { selectReceivedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { useSessionContext } from '@contexts/SessionContext';
import { getUtmParams } from '@utils/utmParams';
import { leadCaptureSchema } from '@components/LeadCaptureForm/utils/validationSchema';
import DialogHeader from '@shared/components/overlays/DialogHeader/DialogHeader';
import LeadCaptureForm from '@components/LeadCaptureForm/LeadCaptureForm';
import type { CustomerType, LeadCaptureFormValues } from '@components/LeadCaptureForm/LeadCaptureForm.types';
import LeadCaptureConfirmationDialog from '@components/modals/LeadCaptureConfirmationDialog/LeadCaptureConfirmationDialog';
import { buildLeadSubmissionPayload } from '@components/modals/LeadCaptureDialog/buildLeadSubmissionPayload';
import { useFireLeadStartedOnOpen } from '@components/modals/LeadCaptureDialog/hooks/useFireLeadStartedOnOpen/useFireLeadStartedOnOpen';
import { useLeadCaptureSubmitError } from '@components/modals/LeadCaptureDialog/hooks/useLeadCaptureSubmitError';

export interface LeadCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
  contextPayload?: Record<string, unknown>;
  contextMessageOverride?: string;
  successActionLabel?: string;
  onSuccessAction?: () => void;
}

const LeadCaptureDialog = ({
  open,
  onClose,
  onSuccess,
  title = 'Request Novus',
  contextPayload,
  contextMessageOverride,
  successActionLabel,
  onSuccessAction,
}: LeadCaptureDialogProps) => {
  const { sessionId } = useSessionContext();

  const { customerType, onNet, qualificationStatus, capturedAddress, leadCaptureReason, receivedAddressId } =
    useQualificationFlowStore(
      useShallow((s) => ({
        customerType: s.customerType,
        onNet: s.onNet,
        qualificationStatus: s.qualificationStatus,
        capturedAddress: s.capturedAddress,
        leadCaptureReason: s.leadCaptureReason,
        receivedAddressId: selectReceivedAddressId(s),
      }))
    );

  const [submitted, setSubmitted] = useState(false);
  const [submittedFormLeadId, setSubmittedFormLeadId] = useState<string>('');
  const { submitError, handleSubmitError, clearSubmitError } = useLeadCaptureSubmitError();

  const getContextMessage = (customerType: CustomerType): string => {
    if (customerType === 'BUSINESS') return CONTEXT_MESSAGES.BUSINESS;

    switch (leadCaptureReason) {
      case LeadCaptureReason.PLANNED:
        return CONTEXT_MESSAGES.PLANNED;
      case LeadCaptureReason.CONSTRUCTION:
        return CONTEXT_MESSAGES.CONSTRUCTION;
      case LeadCaptureReason.NON_BOOKABLE:
        return CONTEXT_MESSAGES.NON_BOOKABLE;
      case LeadCaptureReason.OFF_NET:
      case LeadCaptureReason.ADDRESS_NOT_FOUND:
      case LeadCaptureReason.SYSTEM_ERROR:
        return CONTEXT_MESSAGES.ADDRESS_NOT_FOUND;
      default:
        // contactUs, direct (shouldn't be residential), or null
        return CONTEXT_MESSAGES.ADDRESS_NOT_FOUND;
    }
  };

  const analytics = useAnalytics();

  useFireLeadStartedOnOpen(open, sessionId, { receivedAddressId, leadCaptureReason });

  const { submitLead, isPending } = useSubmitLeadCapture(sessionId || '', {
    onSuccess: (data) => {
      clearSubmitError();
      setSubmitted(true);
      setSubmittedFormLeadId(data.id);
      if (!sessionId) return;
      analytics.fire('trackLeadSubmitted', {
        sessionId,
        leadRequestId: data.id,
        leadForm: formik.values,
        utmParams: getUtmParams(window.location.search),
        receivedAddressId,
      });
    },
    onError: handleSubmitError,
  });

  const formik = useFormik<LeadCaptureFormValues>({
    enableReinitialize: true,
    initialValues: {
      ...INITIAL_LEAD_CAPTURE_VALUES,
      customerType: customerType || 'RESIDENTIAL',
      onNet: onNet,
      qualificationStatus: qualificationStatus || '',
      streetNumber: capturedAddress?.streetNumber || '',
      streetName: capturedAddress?.streetName || '',
      unitNumber: capturedAddress?.unitNumber || '',
      city: capturedAddress?.city || '',
      postalCode: capturedAddress?.postalCode || '',
      state: capturedAddress?.province
        ? (PROVINCES_OPTIONS.find((p) => {
            const input = capturedAddress!.province.toUpperCase();
            const mappedValue = PROVINCE_ABBREVIATION_MAP[input];
            if (mappedValue) return p.value === mappedValue;
            return p.label.toLowerCase() === input.toLowerCase() || p.value.toLowerCase() === input.toLowerCase();
          }) ?? null)
        : null,
    },
    validationSchema: leadCaptureSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      submitLead(buildLeadSubmissionPayload(values, contextPayload));
    },
  });

  const handleClose = () => {
    onClose();
  };

  const handleSuccessAction = () => {
    setSubmitted(false);
    setSubmittedFormLeadId('');
    formik.resetForm();
    onSuccessAction?.();
  };

  const handleSubmitClick = useCallback(async () => {
    clearSubmitError();
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(setNestedObjectValues(formik.values, true));
      // Scroll to first error field after DOM updates with error styles
      setTimeout(() => {
        const root = document;
        const firstError = root.querySelector('.Mui-error') as HTMLElement;
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }
    formik.submitForm();
  }, [formik]);

  const handleConfirmationClose = () => {
    setSubmitted(false);
    formik.resetForm();
    onSuccess();
  };

  if (submitted) {
    return (
      <LeadCaptureConfirmationDialog
        open={open}
        onClose={handleConfirmationClose}
        leadId={submittedFormLeadId}
        actionLabel={successActionLabel}
        onAction={handleSuccessAction}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      variant="fullPageMobile"
      data-backdrop-variant="blurred"
      className="lead-capture-dialog"
    >
      {/* Sticky header: title + customer type toggle */}
      <DialogHeader onClose={handleClose} title={title} variant="title.dialogCondensed" />

      {/* Scrollable body */}
      <DialogContent>
        <LeadCaptureForm
          formik={formik}
          contextMessage={contextMessageOverride ?? getContextMessage(formik.values.customerType)}
        />
      </DialogContent>

      <DialogActions>
        {submitError && (
          <Alert
            severity="error"
            icon={false}
            onClose={clearSubmitError}
            className="lead-capture-error-alert"
          >
            <Stack>
              <Typography className="lead-capture-error-headline">{submitError.headline}</Typography>
              <Typography className="lead-capture-error-body">{submitError.body}</Typography>
            </Stack>
          </Alert>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          width="100%"
          gap="15px"
          className="lead-capture-actions"
        >
          <Button variant="outlined" onClick={handleClose}>
            CANCEL
          </Button>
          <Button variant="contained" onClick={handleSubmitClick} disabled={isPending}>
            {isPending ? <CircularProgress size={18} color="inherit" /> : 'SUBMIT'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default LeadCaptureDialog;
